/* lib/overlay.jsx — общие хелперы оверлеев: focus-trap, body-scroll-lock, ESC.
   Используются Drawer / Modal / ConfirmDialog. */

import React from 'react';

  const { useEffect, useLayoutEffect, useRef } = React;
  // коммит-эффект в браузере (до отрисовки); на сервере — обычный
  const useCommitEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  let lockCount = 0;
  function lockScroll() {
    if (lockCount === 0) {
      const sw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (sw > 0) document.body.style.paddingRight = sw + 'px';
    }
    lockCount++;
    return () => {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) { document.body.style.overflow = ''; document.body.style.paddingRight = ''; }
    };
  }

  const FOCUSABLE = 'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

  // Вернуть фокус туда, откуда открыли оверлей, — только если он потерян (body, узел
  // оверлея) и прежний элемент ещё в документе. Если фокус уже осознанно переведён
  // (поле с ошибкой после «Сохранить» в диалоге сторожа, строка реестра по id после
  // закрытия drawer'а), не отнимаем его; на размонтированный узел не ставим (K-15).
  function shouldRestoreFocus(active, node, prev, doc) {
    if (!prev || typeof prev.focus !== 'function' || !doc || !doc.body || !doc.body.contains(prev)) return false;
    return !active || active === doc.body || !doc.body.contains(active) || (!!node && node.contains(active));
  }

  // useFocusTrap(ref, { active, onEscape, initialFocus }) — ловит Tab внутри узла
  function useFocusTrap(ref, { active = true, onEscape, autoFocusSelector } = {}) {
    const prevFocus = useRef(null);
    // onEscape — текущий на момент нажатия: слушатель живёт, пока active, а обработчик у
    // диалога меняется (сторож заперт на время записи формы — закрыть нельзя, запись кончилась
    // — можно; со старым замыканием Esc после разблокировки молчал)
    const onEscapeRef = useRef(onEscape);
    onEscapeRef.current = onEscape;
    // начальный фокус — СРАЗУ, в коммите (layout-эффект), а не таймером: Esc, нажатый сразу
    // после открытия, достаётся оверлею, а не сетке под ним (верификатор B, P9 оракула POS)
    useCommitEffect(() => {
      if (!active) return;
      const node = ref.current;
      if (!node) return;
      prevFocus.current = document.activeElement;

      // начальный фокус: autoFocusSelector → [data-autofocus] → первый фокусируемый → сама
      // панель. Содержимое уже поставило фокус внутрь (редактор — на своё название) — не
      // трогаем; сразу не удалось (узел ещё не фокусируем) — ещё раз после отрисовки.
      // Выключенная цель (диалог сторожа заперт на время записи формы: все кнопки disabled)
      // фокус не примет — пропускаем; нет ни одного фокусируемого — фокус на панель (div без
      // tabindex его не берёт — ставим -1): иначе фокус оставался ЗА модальным окном, и Esc/Tab
      // доставались drawer'у под ним (kitchen-ux D1b круг 3, «Esc во время записи»).
      const usable = (el) => !!el && !el.disabled;
      const focusTarget = [autoFocusSelector ? node.querySelector(autoFocusSelector) : null, node.querySelector('[data-autofocus]')]
        .find(usable) || null;
      const first = focusTarget || node.querySelector(FOCUSABLE) || node;
      if (first === node && typeof node.hasAttribute === 'function' && typeof node.setAttribute === 'function' && !node.hasAttribute('tabindex')) {
        node.setAttribute('tabindex', '-1');
      }
      const place = () => {
        if (!focusTarget && node.contains(document.activeElement) && document.activeElement !== node) return true;
        try { first.focus(); } catch (e) {}
        return node.contains(document.activeElement);
      };
      if (!place()) setTimeout(place, 0);

      const onKey = (e) => {
        if (e.key === 'Escape') { e.stopPropagation(); if (onEscapeRef.current) onEscapeRef.current(); return; }
        if (e.key !== 'Tab') return;
        const items = Array.from(node.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
        if (!items.length) { e.preventDefault(); return; }
        const firstEl = items[0], lastEl = items[items.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
        else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
      };
      node.addEventListener('keydown', onKey);
      return () => {
        node.removeEventListener('keydown', onKey);
        const prev = prevFocus.current;
        setTimeout(() => {
          if (!shouldRestoreFocus(document.activeElement, node, prev, document)) return;
          try { prev.focus(); } catch (e) {}
        }, 0);
      };
    }, [active]);
  }

  // useScrollLock(active)
  function useScrollLock(active) {
    useEffect(() => {
      if (!active) return;
      const release = lockScroll();
      return release;
    }, [active]);
  }

  export const overlay = { useFocusTrap, useScrollLock, FOCUSABLE, shouldRestoreFocus };

  // общий портал-узел оверлеев — определён в group 1, до Modal/Drawer/Popover
  function getOverlayRoot() {
    let el = document.getElementById('overlay-root');
    if (!el) { el = document.createElement('div'); el.id = 'overlay-root'; document.body.appendChild(el); }
    return el;
  }

export { getOverlayRoot };
